import { updatePageProps } from '@eventbrite/datalayer-library';
import { FormattedEvent } from '@eventbrite/event-renderer';
import { getWindowObject } from '@eventbrite/feature-detection';
import { getCanonicalSearchUrl } from '@eventbrite/redux-destination';
import {
    setAttributes,
    trackEventFromState,
    trackInitialPageView,
} from '@eventbrite/site-analytics';
import { logEvent } from '@eventbrite/statsig';
import { $FixMe } from '@eventbrite/ts-utils';
import isEmpty from 'lodash/isEmpty';
import { formatUrl, parseQuery } from 'url-lib';
import {
    GA_ENTER_MAP_VIEW,
    GA_PAGE_LOAD_ACTION,
    GA_RUN_SEARCH_ACTION,
    GA_SEARCH_CATEGORY,
    HEAP_CITY_BROWSE_PAGE_VIEW,
    HEAP_SEARCH_PAGE_VIEW,
    SEARCH_EVENT_NAVIGATION,
} from '../../constants/analytics';
import { getSearchId } from '../../pages/search/utils/analytics';
import { DiscoverState, EventSearch, PaginationData } from '../../types';
import { logDiscoveryRequest } from '../api/analytics';
import { SearchMetrics } from '../api/search';
import {
    trackInternalAppClick,
    transformSearchAndDiscovery,
} from '../utils/analytics';
import { CLEAN_TRACKING_DATA } from './search';

const MAX_EVENTS_FOR_GA = 10;
const processTagsForGA = (tagType: string, eventSearch: { tags?: string[] }) =>
    eventSearch.tags && eventSearch.tags.length
        ? eventSearch.tags.filter((tag) => tag.indexOf(tagType) > -1).join(',')
        : '';

const processDatesForCustomDimension = (eventSearch: {
    dateRange?: { from?: string; to?: string };
    dates?: string[];
}) => {
    if (
        eventSearch.dateRange &&
        eventSearch.dateRange.from &&
        eventSearch.dateRange.to
    ) {
        return `${eventSearch.dateRange.from},${eventSearch.dateRange.to}`;
    }

    if (Array.isArray(eventSearch.dates)) {
        return eventSearch.dates[1];
    }

    return '';
};

// There are some dimensions we want on all Search pageviews in GA
// The definitions come from here:
// https://analytics.google.com/analytics/web/#/a141520w171290p95297140/admin/custom-dimensions/m-content-dimensionsContent.rowShow=100&m-content-dimensionsContent.rowStart=0&m-content-dimensionsContent.sortColumnId=index&m-content-dimensionsContent.sortDescending=false
// The constants map to dimensions here:
// https://github.com/eventbrite/js-utils/blob/master/src/analytics/constants.js
export const getSearchCustomDimensionsAndMetrics = (
    eventSearch: Pick<
        EventSearch,
        'bbox' | 'q' | 'price' | 'page' | 'tags' | 'dateRange' | 'dates'
    >,
    slug: any,
    eventIds: any[],
) => ({
    searchLocation: eventSearch.bbox || slug,
    // first half of events (due to GA action size limits, we can only send a max of 10 event IDs per dimension field)
    searchEventIds1: eventIds.slice(0, MAX_EVENTS_FOR_GA).join(','),
    // second half of events - right now we assume that there are 20 events on the page,
    // if we change it more, we will need more dimensions to accommodate it
    searchEventIds2: eventIds
        .slice(MAX_EVENTS_FOR_GA, MAX_EVENTS_FOR_GA * 2)
        .join(','),
    searchQuery: eventSearch.q,
    searchDates: processDatesForCustomDimension(eventSearch),
    searchPrice: eventSearch.price || '',
    searchCategory: processTagsForGA('EventbriteCategory', eventSearch),
    searchFormat: processTagsForGA('EventbriteFormat', eventSearch),
    searchSort: '',
    searchResultCount: eventIds.length,
    searchPagination: eventSearch.page,
});

export const trackEventClickInternal =
    (data: any) =>
    (
        _dispatch: any,
        getState: () => {
            search: { searchId: any; eventSearch: any };
            app: { guestId: any };
        },
    ) => {
        const {
            search: { searchId, eventSearch },
            app: { guestId },
        } = getState();

        const searchMetricId = getSearchId({ eventSearch, guestId });

        trackInternalAppClick({ searchId, searchMetricId, guestId, ...data });
    };

const _getCurrentPath = (
    eventSearch: Pick<EventSearch, 'q'>,
    serverUrl: any,
    slug: any,
) => {
    const location = getWindowObject('location');

    let currentPath;

    if (location) {
        currentPath = location.href || '';
    } else {
        currentPath = '';
    }

    const canonicalSearchUrl = getCanonicalSearchUrl({
        ...eventSearch,
        slug,
    });

    const path = `${serverUrl}${canonicalSearchUrl}`;

    if (eventSearch.q) {
        const queryParams = currentPath.includes('?')
            ? parseQuery(currentPath)
            : {};

        currentPath = formatUrl(path, {
            ...queryParams,
            q: eventSearch.q,
        });
    }
    return currentPath;
};

/*
When a page is viewed there are several logs that are notified.
 */
export const trackCityBrowsePageView =
    () => (_dispatch: any, getState: Function) => {
        const {
            app: { serverUrl },
            gaSettings,
            search: { eventSearch },
            location: { slug },
        }: DiscoverState = getState();

        const currentPath = _getCurrentPath(eventSearch, serverUrl, slug);

        setAttributes({ location: currentPath });
        logEvent(HEAP_CITY_BROWSE_PAGE_VIEW);
        trackInitialPageView(gaSettings);
    };

/*
When a page is viewed there are several logs that are notified.
 */
export const trackSearchPageView =
    (
        events: FormattedEvent[],
        pagination: PaginationData,
        topMatchShown: boolean,
    ) =>
    (_dispatch: any, getState: Function) => {
        const {
            app: { guestId, publicId, serverUrl, sessionId },
            gaSettings,
            search: { eventSearch },
            location: { slug },
            auth: { isAuthenticated },
        }: DiscoverState = getState();

        let gaDimensions: any = {};
        const eventIds = events.map((event) => event.id);

        const searchId = logDiscoveryRequest({
            eventSearch,
            publicUserId: publicId,
            guestId,
            locationSlug: slug,
            //userGenerated is always unknown at this point in code
            // - did the user change the search or just click a SEO link?
            userGenerated: null,
            navigation: SEARCH_EVENT_NAVIGATION,
            action: GA_PAGE_LOAD_ACTION,
            // variant can be used to track experiments
            resultIds: eventIds,
            numResults: pagination.objectCount,
        });

        if (searchId) {
            gaDimensions.searchId = searchId;
        }

        if (isAuthenticated) {
            gaDimensions.sessionId = sessionId;
        }

        gaDimensions = {
            ...gaDimensions,
            ...getSearchCustomDimensionsAndMetrics(eventSearch, slug, eventIds),
        };

        const eventData = {
            ...eventSearch,
            sessionId,
            topMatchShown,
        };

        const location = {
            placeId: eventSearch?.places?.[0] || '',
            name: slug || '',
        };

        const transformedSearchAndDiscoveryPage = transformSearchAndDiscovery(
            eventData,
            location,
        );
        updatePageProps(
            {
                eventName: GA_RUN_SEARCH_ACTION,
                page: transformedSearchAndDiscoveryPage,
            },
            'snake',
        );
        updatePageProps(
            {
                eventName: HEAP_SEARCH_PAGE_VIEW,
                page: transformedSearchAndDiscoveryPage,
            },
            'snake',
        );

        const currentPath = _getCurrentPath(eventSearch, serverUrl, slug);

        setAttributes({ location: currentPath });
        logEvent(HEAP_SEARCH_PAGE_VIEW);
        trackInitialPageView(gaSettings, { dimensions: gaDimensions });
    };

export const trackEnterMapView =
    () => (_dispatch: any, getState: () => any) => {
        const state = getState();
        const {
            location: { location },
        } = state;

        trackEventFromState(state, {
            label: location,
            category: GA_SEARCH_CATEGORY,
            action: GA_ENTER_MAP_VIEW,
        });
    };

export const logSearchMetrics =
    (metrics: SearchMetrics | undefined) =>
    (dispatch: Function, getState: Function) => {
        const state: DiscoverState = getState();
        const {
            app: { guestId, publicId },
            search: { eventSearch, gaTrackingData },
            location: { slug },
        } = state;

        if (isEmpty(gaTrackingData)) {
            return;
        }

        const action = gaTrackingData
            .map((d: { action: string }) => d.action)
            .sort()
            .join(',');

        const searchId = logDiscoveryRequest({
            eventSearch,
            guestId,
            locationSlug: slug,
            publicUserId: publicId,
            userGenerated: true,
            navigation: SEARCH_EVENT_NAVIGATION,
            action,
            // variant can be used to track experiments
            variant: null,
        });

        gaTrackingData
            .map(addMetricsToTrackingData(metrics, searchId))
            .forEach((trackingData) =>
                trackEventFromState(state, trackingData),
            );

        dispatch({ type: CLEAN_TRACKING_DATA });
    };

function addMetricsToTrackingData(
    metrics?: SearchMetrics,
    searchId?: string | null,
) {
    return (trackingData: $FixMe) => {
        if (searchId) {
            return {
                ...trackingData,
                dimensions: {
                    ...trackingData.dimensions,
                    searchId,
                },
                metrics,
            };
        } else {
            return trackingData;
        }
    };
}
